import { format, parseISO } from "date-fns";
import { DATE_FORMATS, DEFAULT_DATE_FORMAT } from "./constants";

const quarterStart = (dt?: Date) => {
  if (!dt) {
    dt = new Date();
  }
  const year = dt.getFullYear();
  const month = dt.getMonth();

  // Months are 0 indexed.
  const APR = 3;
  const JUL = 6;
  const OCT = 9;

  if (month < APR) {
    return new Date(year, 0, 1);
  } else if (month < JUL) {
    return new Date(year, 3, 1);
  } else if (month < OCT) {
    return new Date(year, 6, 1);
  } else {
    return new Date(year, 9, 1);
  }
};

const previousQuarterEnd = (dt?: Date) => {
  if (!dt) {
    dt = new Date();
  }
  return new Date(quarterStart(dt).getTime() - 1);
};

const getQuarter = (dt: Date) => {
  return Math.floor(dt.getMonth() / 3 + 1);
};

const getLastNQuarters = (n = 4) => {
  const quarters = [...Array(n)];

  for (let i = 0; i < n; i++) {
    const prevQuarterEnd =
      i < 1 ? previousQuarterEnd() : previousQuarterEnd(quarters[i - 1].quarterEnd);
    quarters[i] = {
      name: "Q" + getQuarter(prevQuarterEnd).toString(),
      year: prevQuarterEnd.getFullYear(),
      quarterStart: quarterStart(prevQuarterEnd),
      quarterEnd: prevQuarterEnd,
    };
  }
  return quarters;
};

export const toStandardFormat = (
  maybeDate: Date | string,
  dateFormatOverride?: string,
): string => {
  const date = typeof maybeDate === "string" ? parseISO(maybeDate) : maybeDate;
  // In case date is invalid and format fails, return an empty string
  try {
    return format(date, dateFormatOverride || DATE_FORMATS[DEFAULT_DATE_FORMAT].long);
  } catch {
    return "";
  }
};

export const toISOFormat = (maybeDate: Date | string): string => {
  const date = typeof maybeDate === "string" ? parseISO(maybeDate) : maybeDate;
  return format(date, "yyyy-MM-dd");
};

const toISOLocalString = (dt: Date) => {
  const offset = -dt.getTimezoneOffset();
  const offsetSign = offset < 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const datetime = new Date(dt.getTime() + offset * 60 * 1000).toISOString();
  const offsetHours = Math.floor(absOffset / 60).toLocaleString("en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false,
  });
  const offsetMinutes = (absOffset % 60).toLocaleString("en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false,
  });
  return datetime.replace("Z", `${offsetSign}${offsetHours}:${offsetMinutes}`);
};

// Example output format: 'November 27, 2023 at 2:34 PM'
const formatFullDateAndTime = (dateTime: string, dateFormatOverride?: string) => {
  const date = parseISO(dateTime);
  return format(date, dateFormatOverride || "MMMM d, yyyy 'at' h:mm a");
};

const formatFullDateAndTimeWithLocalTimezone = (
  dateTime: string,
  dateFormatOverride?: string,
  includeTzName = false,
) => {
  let date = parseISO(dateTime);
  const offset = -date.getTimezoneOffset();
  date = new Date(date.getTime() + offset * 60 * 1000);
  let formattedDate = format(date, dateFormatOverride || "MMMM d, yyyy 'at' h:mm a");
  formattedDate = formattedDate.replace(/\s*AM/g, "am").replace(/\s*PM/g, "pm"); // remove blank space and make lowercase

  if (includeTzName) {
    const timeZoneName = date
      .toLocaleDateString(undefined, { timeZoneName: "short" })
      .split(", ")[1];
    formattedDate += ` ${timeZoneName}`;
  }
  return formattedDate;
};

// Newer version of formatFullDateAndTimeWithLocalTimezone that uses Intl.DateTimeFormat
export const formatFullDateAndTimeWithLocalTimezoneV2 = (
  dateTime: string,
  dateFormatOverride?: string,
  includeTzName = false,
) => {
  const date = parseISO(dateTime);
  const pattern = dateFormatOverride || "MMMM d, yyyy 'at' h:mm aaa";
  let formattedDate = format(date, pattern);

  if (includeTzName) {
    const tzName = new Intl.DateTimeFormat(undefined, {
      timeZoneName: "short",
    })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName")?.value;

    if (tzName) {
      formattedDate += ` ${tzName}`;
    }
  }

  return formattedDate;
};

// Example output format: 'Nov 27, 2023'
const formatShortFormDate = (dateTime: string | null, dateFormatOverride?: string) => {
  if (!dateTime) return "";
  const date = parseISO(dateTime);
  return format(date, dateFormatOverride || "MMM d, yyyy");
};

// For an ISO string that encodes a calendar date rather than a real instant (e.g. a due date
// picked from a date-only control and stored as UTC midnight) -- decoding it with local Date
// getters shifts the day backward in any timezone ahead of UTC. Read it back via UTC fields
// instead, into a Date at local midnight for that same calendar day.
const toCalendarDate = (isoString: string): Date => {
  const utc = new Date(isoString);
  return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
};

const formatShortFormDateInLocalTimezone = (dateTime: string, dateFormatOverride?: string) => {
  // adjust date to local timezone but only show the date portion
  let date = parseISO(dateTime);
  const offset = -date.getTimezoneOffset();
  date = new Date(date.getTime() + offset * 60 * 1000);
  return format(date, dateFormatOverride || "MMM d, yyyy");
};

// Format date as MM/DD/YYYY
const toMMDDYYYYFormat = (dateTime: Date): string => {
  return format(dateTime, "MM/dd/yyyy");
};

// Format date as "Month Day, Year" (e.g., "November 26, 2025")
export const formatLongDate = (date: Date | string): string => {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const timeDifferenceInMS = (a: Date, b: Date): number => {
  return +new Date(a) - +new Date(b);
};

const normalizeToDateOnly = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const isDateInRange = (date: Date, startDate: Date, endDate: Date): boolean => {
  const dateOnly = normalizeToDateOnly(date);
  const startOnly = normalizeToDateOnly(startDate);
  const endOnly = normalizeToDateOnly(endDate);
  return dateOnly >= startOnly && dateOnly <= endOnly;
};

const getTimeSinceInMinutes = (date: Date): number => {
  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  return Math.floor(diffTime / (1000 * 60));
};

const getTimeSinceInHours = (date: Date): number => {
  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60));
};

const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Returns the date to compare against today when determining whether a document has expired.
 *
 * For document types where the user enters a date of issue or signature (e.g. AML reliance
 * letter, address verification, W-series tax forms), the backend derives the actual expiry
 * (e.g. date of signature + 3 years for W-8BEN) and stores it in the document's top-level
 * `expiryDate` field. The form field itself continues to display the original date of issue.
 *
 * This function returns the backend-computed expiry when available so that the "document has
 * expired" indicator is based on the true expiry rather than the raw issue date — which would
 * always appear expired since issue dates can never be in the future.
 *
 * For ID documents the form field already captures the real expiry date, so
 * `documentExpiryDate` and `fieldDate` are equal and either value is correct.
 *
 * @param fieldDate - The date value currently shown in the form field (date of issue/signature).
 * @param documentExpiryDate - The backend-computed expiry from the document's top-level
 *   `expiryDate` field, parsed into a Date. May be undefined if the document has no expiry.
 * @returns `documentExpiryDate` when present; falls back to `fieldDate`.
 */
export const getEffectiveExpiryDate = (
  fieldDate: Date | undefined,
  documentExpiryDate: Date | undefined,
): Date | undefined => documentExpiryDate ?? fieldDate;

/**
 * Returns true if the given value represents a calendar date that is strictly before today.
 * Accepts a Date object or a date/datetime string. Returns false for null, undefined, or
 * unparseable values.
 *
 * Comparison is done at calendar-day granularity (local timezone) so a date equal to
 * today is not considered expired.
 */
const isDateExpired = (value: unknown): boolean => {
  if (!value) return false;
  const date = value instanceof Date ? value : new Date(value as string);
  if (isNaN(date.getTime())) return false;
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return dateMidnight < todayMidnight;
};

export {
  getLastNQuarters,
  quarterStart,
  previousQuarterEnd,
  toISOLocalString,
  formatFullDateAndTime,
  formatFullDateAndTimeWithLocalTimezone,
  formatShortFormDate,
  formatShortFormDateInLocalTimezone,
  toCalendarDate,
  toMMDDYYYYFormat,
  timeDifferenceInMS,
  isDateInRange,
  isSameDay,
  isDateExpired,
  getTimeSinceInMinutes,
  getTimeSinceInHours,
};
